/**
 * The paged search a collector uses to choose works for a viewing room.
 *
 * Its own model rather than a corner of `viewingRooms.model.ts`, because a
 * query, an offset and a page of results are meaningless on the rooms list.
 * What it borrows from there is the shape of a choice and `isChosen`, so a
 * work ticked here is the same thing a chip on the form names. The fetching
 * and the debounce are `useWorkPickerController.ts`'s; the words and page
 * size are `workPicker.constants.ts`'s.
 *
 * A page of results carries the search it answered, and `resolvePicker`
 * compares that key against the live one at render time — a response for a
 * query the user has typed past is discarded by derivation, not by a boolean
 * captured in a closure.
 */

import type { WorkPickerRow } from "@ondo/shared";

import type { Choice } from "../lib/choices.ts";
import { MAX_CHOSEN } from "./viewingRooms.constants.ts";
import { isChosen } from "./viewingRooms.model.ts";
import { PAGE_SIZE, WORK_PICKER_COPY } from "./workPicker.constants.ts";

/** Which search a set of results belongs to. Held with them, never beside them. */
export interface SearchKey {
  query: string;
  offset: number;
}

/** A page, as it came back. */
export interface Results {
  key: SearchKey;
  rows: WorkPickerRow[];
  /** Every work that matched, not the number on this page. */
  total: number;
}

export interface PickerRow {
  id: string;
  /** 'Fat Car — Erwin Wurm, 2001'. The label a chip will carry if it is chosen. */
  label: string;
  /** The medium, or null. */
  sublabel: string | null;
  imageUrl: string | null;
  selected: boolean;
  /** True when unselected and the room is already as full as it may be. */
  disabled: boolean;
}

export interface PageView {
  /** "Showing 25 to 48 of 312" */
  label: string;
  canPrev: boolean;
  canNext: boolean;
  prevLabel: string;
  nextLabel: string;
}

/**
 * What the result region shows.
 *
 * A union over the RESULTS rather than over the whole picker, deliberately. The
 * running list of what you have chosen has to be visible in every state — a
 * union over the modal would put it inside one variant and take it away in the
 * others, and a `loading` variant that replaced the rows would blank the list on
 * every keystroke.
 */
export type ResultsView =
  | { kind: "first-load"; message: string }
  | { kind: "error"; message: string }
  /** Owns no works at all. Refining a search cannot help. */
  | { kind: "nothing"; message: string }
  /** Owns works; none match this query. */
  | { kind: "no-matches"; message: string }
  | { kind: "rows"; rows: PickerRow[]; page: PageView; busy: boolean };

export interface WorkPickerView {
  open: boolean;
  title: string;
  /** What is in the box right now, not what was last searched for. */
  query: string;
  /** The footer, in every state. */
  selected: Choice[];
  /** "3 of 500 works chosen" */
  countLabel: string;
  /** True at the cap: rows disable rather than failing at submit. */
  atCapacity: boolean;
  /** One polite live region's whole sentence, composed here rather than in the view. */
  announcement: string;
  results: ResultsView;
}

/**
 * The picker.
 *
 * `results` carries the search it belongs to, and this compares that key against
 * the live one at render time. A response for a query the user has already typed
 * past is therefore discarded by DERIVATION rather than by a boolean captured in
 * a closure — the same reason `useViewingRoomsController` stores its visit list
 * alongside the room id rather than clearing it when the record changes.
 */
export function resolvePicker(input: {
  open: boolean;
  typed: string;
  search: SearchKey;
  results: Results | null;
  failed: boolean;
  images: Map<string, string>;
  selected: Choice[];
}): WorkPickerView {
  const fresh = input.results !== null && sameSearch(input.results.key, input.search);
  const atCapacity = input.selected.length >= MAX_CHOSEN;

  return {
    open: input.open,
    title: WORK_PICKER_COPY.title,
    query: input.typed,
    selected: input.selected,
    countLabel: `${String(input.selected.length)} of ${String(MAX_CHOSEN)} works chosen`,
    atCapacity,
    announcement: announce(input.results, fresh, input.selected.length),
    results: toResults({
      results: input.results,
      fresh,
      failed: input.failed,
      images: input.images,
      selected: input.selected,
      atCapacity,
      searching: input.search.query !== "",
    }),
  };
}

function toResults(input: {
  results: Results | null;
  fresh: boolean;
  failed: boolean;
  images: Map<string, string>;
  selected: Choice[];
  atCapacity: boolean;
  searching: boolean;
}): ResultsView {
  if (input.failed) return { kind: "error", message: WORK_PICKER_COPY.failed };
  // Nothing has ever come back. The only state with nothing to show.
  if (input.results === null) {
    return { kind: "first-load", message: WORK_PICKER_COPY.firstLoad };
  }

  if (input.results.total === 0) {
    // Two different sentences, and the difference matters: telling somebody who
    // owns nothing to refine their search sends them after a result that cannot
    // exist. Only a query that is actually narrowing can produce "no matches".
    return input.searching
      ? { kind: "no-matches", message: WORK_PICKER_COPY.noMatches }
      : { kind: "nothing", message: WORK_PICKER_COPY.nothing };
  }

  return {
    kind: "rows",
    rows: input.results.rows.map((row) => toPickerRow(row, input)),
    page: toPage(input.results.key.offset, input.results.rows.length, input.results.total),
    // Stale rows stay on screen while the next page is in flight. Removing them
    // would flash the list empty on every keystroke.
    busy: !input.fresh,
  };
}

function toPickerRow(
  row: WorkPickerRow,
  context: { images: Map<string, string>; selected: Choice[]; atCapacity: boolean },
): PickerRow {
  const selected = isChosen(context.selected, row.id);

  return {
    id: row.id,
    label: labelFor(row),
    sublabel: row.medium,
    imageUrl: context.images.get(row.id) ?? null,
    selected,
    // Never a selected row: you must always be able to take one back out.
    disabled: context.atCapacity && !selected,
  };
}

/** 'Fat Car — Erwin Wurm, 2001'. Kept from the flat picker this replaced. */
export function labelFor(row: Pick<WorkPickerRow, "title" | "attributedName" | "year">): string {
  return row.year
    ? `${row.title} — ${row.attributedName}, ${row.year}`
    : `${row.title} — ${row.attributedName}`;
}

/**
 * Where in the match set this page sits.
 *
 * `first` is derived from the offset and `last` from how many rows actually came
 * back rather than from the page size, so a short final page reads correctly
 * instead of claiming rows it does not have.
 */
export function toPage(offset: number, shown: number, total: number): PageView {
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + shown;

  return {
    label: `Showing ${String(first)} to ${String(last)} of ${String(total)}`,
    canPrev: offset > 0,
    // `<`, not `<=`. With total 24 and offset 0 the last row shown is 24, there
    // is nothing after it, and a `<=` here would offer an empty next page — the
    // off-by-one that ships in every hand-written paginator.
    canNext: last < total,
    prevLabel: WORK_PICKER_COPY.previous,
    nextLabel: WORK_PICKER_COPY.next,
  };
}

/**
 * The offset a Previous or Next press lands on.
 *
 * Clamped at both ends, because the offset can outlive the result set it was
 * valid for: works removed elsewhere shrink the total under a page the user is
 * already on. `nextOffset` refuses to walk past the end rather than fetching a
 * page that can only be empty.
 */
export function prevOffset(offset: number): number {
  return Math.max(0, offset - PAGE_SIZE);
}

export function nextOffset(offset: number, total: number): number {
  return offset + PAGE_SIZE >= total ? offset : offset + PAGE_SIZE;
}

/**
 * The one sentence a screen reader is given.
 *
 * One region rather than several: two competing polite regions announce nothing
 * coherent. It deliberately does NOT echo the query — repeating what somebody
 * just typed back at them is noise, and a 120-character query would be read out
 * in full.
 */
function announce(results: Results | null, fresh: boolean, chosen: number): string {
  const picked = `${String(chosen)} chosen.`;
  if (results === null || !fresh) return picked;
  if (results.total === 0) return `No works match. ${picked}`;

  const page = toPage(results.key.offset, results.rows.length, results.total);
  return `${page.label}. ${picked}`;
}

function sameSearch(a: SearchKey, b: SearchKey): boolean {
  return a.query === b.query && a.offset === b.offset;
}
